import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Trash2, Vault, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useVaultStore } from "@/stores/billing/vault-store";
import { useBillingStore } from "@/stores/billing/billing-store";
import { VaultAssignment } from "@/components/billing/VaultAssignment";
import type { VaultType, TokenVault } from "@/types/billing";

/**
 * VaultManager
 * Admin-only vault management component for creating, editing, and deleting token vaults.
 * Shows vault list with budget allocations and provides a create dialog.
 */

const VAULT_TYPE_LABELS: Record<VaultType, string> = {
  team: "Team",
  project: "Project",
  personal: "Personal",
  general: "General",
};

const VAULT_TYPE_COLORS: Record<VaultType, string> = {
  team: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  project: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300",
  personal: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
  general: "bg-gray-100 text-gray-700 dark:bg-gray-900/40 dark:text-gray-300",
};

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export const VaultManager: React.FC = () => {
  const {
    vaults,
    isLoading,
    createVault,
    updateVault,
    deleteVault,
  } = useVaultStore();

  const { subscription } = useBillingStore();

  const [createOpen, setCreateOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [expandedVaultId, setExpandedVaultId] = useState<string | null>(null);

  // Create form state
  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState<VaultType>("team");
  const [newBudgetCents, setNewBudgetCents] = useState(0);
  const [newFallthrough, setNewFallthrough] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const planBudgetCents = subscription?.planBudgetCents || 0;
  const totalAllocated = vaults.reduce((sum, v) => sum + v.allocatedBudgetCents, 0);
  const remainingBudget = Math.max(0, planBudgetCents - totalAllocated);

  const resetCreateForm = () => {
    setNewName("");
    setNewType("team");
    setNewBudgetCents(0);
    setNewFallthrough(false);
  };

  const handleCreate = async () => {
    if (!newName.trim() || newBudgetCents <= 0) return;

    setIsSubmitting(true);
    try {
      await createVault({
        name: newName.trim(),
        type: newType,
        allocatedBudgetCents: newBudgetCents,
        allowFallthrough: newFallthrough,
      });
      setCreateOpen(false);
      resetCreateForm();
    } catch {
      // Error handled in store
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteVault(id);
      setDeleteConfirmId(null);
    } catch {
      // Error handled in store
    }
  };

  const handleToggleFallthrough = async (vault: TokenVault) => {
    try {
      await updateVault(vault.id, {
        allowFallthrough: !vault.allowFallthrough,
      });
    } catch {
      // Error handled in store
    }
  };

  if (isLoading && vaults.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Vault className="h-5 w-5" />
            Token Vaults
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Vault className="h-5 w-5" />
              Token Vaults
            </CardTitle>
            <CardDescription>
              Manage AI budget allocation across your organization.
            </CardDescription>
          </div>
          <Dialog open={createOpen} onOpenChange={(open) => {
            setCreateOpen(open);
            if (!open) resetCreateForm();
          }}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-1.5">
                <Plus className="h-4 w-4" />
                Create Vault
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Token Vault</DialogTitle>
                <DialogDescription>
                  Allocate a portion of your plan budget to a new vault.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="vault-name">Vault Name</Label>
                  <Input
                    id="vault-name"
                    placeholder="e.g., Engineering Team"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vault-type">Vault Type</Label>
                  <Select value={newType} onValueChange={(v) => setNewType(v as VaultType)}>
                    <SelectTrigger id="vault-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="team">Team</SelectItem>
                      <SelectItem value="project">Project</SelectItem>
                      <SelectItem value="personal">Personal</SelectItem>
                      <SelectItem value="general">General</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>
                    Budget Allocation: {formatCents(newBudgetCents)}
                  </Label>
                  <Slider
                    value={[newBudgetCents]}
                    onValueChange={([val]) => setNewBudgetCents(val)}
                    min={0}
                    max={remainingBudget}
                    step={100}
                    className="py-2"
                  />
                  <p className="text-xs text-muted-foreground">
                    Available: {formatCents(remainingBudget)} of {formatCents(planBudgetCents)} total plan budget
                  </p>
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="vault-fallthrough">Allow Fallthrough</Label>
                    <p className="text-xs text-muted-foreground">
                      Fall back to other vaults when budget is exhausted
                    </p>
                  </div>
                  <Switch
                    id="vault-fallthrough"
                    checked={newFallthrough}
                    onCheckedChange={setNewFallthrough}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setCreateOpen(false);
                    resetCreateForm();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreate}
                  disabled={!newName.trim() || newBudgetCents <= 0 || isSubmitting}
                >
                  {isSubmitting ? "Creating..." : "Create Vault"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Budget allocation summary */}
        <div className="rounded-lg border border-border bg-muted/50 p-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Total Allocated</span>
            <span className="font-medium text-foreground">
              {formatCents(totalAllocated)} / {formatCents(planBudgetCents)}
            </span>
          </div>
          <Progress
            value={planBudgetCents > 0 ? (totalAllocated / planBudgetCents) * 100 : 0}
            className="mt-2 h-2"
          />
          {totalAllocated > planBudgetCents && (
            <div className="mt-2 flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400">
              <AlertTriangle className="h-3 w-3" />
              Allocations exceed plan budget
            </div>
          )}
        </div>

        <Separator />

        {/* Vault list */}
        {vaults.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">
            No vaults created yet. Create one to start allocating your AI budget.
          </p>
        ) : (
          <div className="space-y-3">
            {vaults.map((vault) => {
              const usagePercent = Math.min(100, Math.max(0, vault.usagePercent));
              return (
                <div key={vault.id} className="space-y-2">
                  <div className="rounded-lg border border-border p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground">{vault.name}</span>
                        <Badge
                          variant="secondary"
                          className={cn("text-xs", VAULT_TYPE_COLORS[vault.type])}
                        >
                          {VAULT_TYPE_LABELS[vault.type]}
                        </Badge>
                        {vault.isDefault && (
                          <Badge variant="outline" className="text-xs">Default</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setExpandedVaultId(
                              expandedVaultId === vault.id ? null : vault.id
                            )
                          }
                        >
                          {expandedVaultId === vault.id ? "Hide" : "Assignments"}
                        </Button>
                        {deleteConfirmId === vault.id ? (
                          <div className="flex items-center gap-1">
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDelete(vault.id)}
                              disabled={isLoading}
                            >
                              Confirm
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setDeleteConfirmId(null)}
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            onClick={() => setDeleteConfirmId(vault.id)}
                            disabled={vault.isDefault}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>

                    <div className="mt-3 grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Budget:</span>{" "}
                        <span className="font-medium text-foreground">
                          {formatCents(vault.allocatedBudgetCents)}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Used:</span>{" "}
                        <span className="font-medium text-foreground">
                          {formatCents(vault.usedBudgetCents)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">Fallthrough:</span>
                        <Switch
                          checked={vault.allowFallthrough}
                          onCheckedChange={() => handleToggleFallthrough(vault)}
                        />
                      </div>
                    </div>

                    <div className="mt-2">
                      <Progress
                        value={usagePercent}
                        className={cn(
                          "h-1.5",
                          usagePercent >= 80
                            ? "[&>div]:bg-red-500"
                            : usagePercent >= 60
                              ? "[&>div]:bg-yellow-500"
                              : "[&>div]:bg-green-500",
                        )}
                      />
                      <p className="mt-1 text-xs text-muted-foreground">
                        {Math.round(usagePercent)}% used
                      </p>
                    </div>
                  </div>

                  {expandedVaultId === vault.id && (
                    <div className="ml-4">
                      <VaultAssignment vault={vault} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
